'use client';

import { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { usePathname } from '@/src/i18n/navigation';

import { getCurrentPageType } from '@/src/lib/clientProfiler';

export const GTM_ROUTE_CHANGE_EVENT = 'hp_route_change';

/**
 * Publishes the page the container should treat as current, on the initial
 * load and on every client navigation.
 *
 * gtm.js evaluates All Pages once, on the document load that booted it, and
 * every App Router navigation after that is same-document, so the container
 * never re-evaluates on its own. GTM also loads on the sitemap hubs, to keep
 * GA4's coverage there, while marketing tags must stay off them (HP-8781) - so
 * the container needs to know both when the route changed and what it is.
 *
 * This deliberately fires on the initial load too. Skipping it would leave the
 * landing page undescribed, and a container condition reading an undefined
 * variable blocks nothing: a session landing on a hub would fire the full
 * marketing set, which is worse than main.
 *
 * Marketing tags gate on `page_type != 'sitemap'`. That has to be the page
 * type rather than the path, because the state and city hubs are `/california`
 * and `/california/los-angeles` while an SRP is `/california/apartments-for-rent`
 * - the two-segment shapes are indistinguishable without the slug catalog.
 * Routes declare what they are, so the exclusion cannot drift. This gates the
 * marketing tags that live in the container; Index Exchange is DataGrail-managed
 * and ships on every route regardless (see `datagrail/scripts.ts`).
 * Undeclared routes push no `page_type`, which reads as "not sitemap" and tags
 * them, the safe default.
 *
 * `page_type` is the bare Client Profiler name - the same list the Marlin
 * web-vitals gauges label with, minus the `hotpads-` prefix those dashboards
 * query on - so container rules and GA4 segment by page kind without a deploy
 * per new surface.
 */
export function useGtmRouteChange() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams?.toString() ?? '';
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: GTM_ROUTE_CHANGE_EVENT,
      page_path: search ? `${pathname}?${search}` : pathname,
      page_type: getCurrentPageType(),
    });
  }, [pathname, search]);
}
